<template>
  <section
    :id="RATING_REVIEWS_SECTION_ID"
    class="py-8 lg:py-10"
  >
    <ProductAnchorTitle
      class="lg:hidden"
      :title="$t('Ratings & reviews')"
    />
    <div class="lg:flex items-center justify-between">
      <ProductAnchorTitle
        class="hidden lg:block"
        :title="$t('Ratings & reviews')"
      />
      <div class="lg:flex items-center">
        <div
          v-if="reviews && reviews.length"
          class="flex items-center"
        >
          <span
            v-if="rating"
            class="text-6xl font-bold"
          >{{ RatingNumber }}</span>
          <div class="ml-4 items-center">
            <ReviewsStars
              :rating="rating"
              class="mb-1"
            />
            <p>
              <strong class="max-w-min">
                {{ $t('Based on X reviews', { count: reviewsCount, _reviews_amount_: reviewsCount }) }}
              </strong>
            </p>
          </div>
        </div>
        <CustomButton
          v-if="!isFormVisible"
          class="lg:ml-5 my-5 lg:w-56"
          type="tertiary"
          size="lg"
          :text="$t('Write a review')"
          data-testid="show-review-form-btn"
          @click="showReviewForm"
        />
      </div>
    </div>
    <transition
      name="sf-fade"
    >
      <ProductAddReview
        v-if="isFormVisible"
        :product="product"
        :email="formEmail"
        :rating="formRating"
      />
    </transition>
    <SortingReviews
      :total-results-amount="reviewsCount"
      :product-id="productId"
    />
    <div class="mt-6 lg:mt-10">
      <ReviewWrapper
        v-for="review, index in reviews"
        :id="`${REVIEW_ID}-${index + 1}`"
        :key="review.id"
        :class="{'mt-12': index > 0}"
        :review="review"
      />
    </div>
    <PaginationReviews
      :total-results-amount="reviewsCount"
      :product-id="productId"
    />
  </section>
</template>

<script lang="ts">


import { reviewGetters, productGetters } from '~/utils/ct';
import type { ProductReview } from '~~/types/product/attribute/ProductReview';
import { REVIEW_ID, RATING_REVIEWS_SECTION_ID } from '~~/constants/pagination';
import floorToOneDecimal from '~~/helpers/floorToOneDecimal';
import type { ProductVariantExtended } from '~~/types/product/ProductVariantExtended';

export default defineComponent({
  name: 'RatingsReviews',

  props: {
    reviews: {
      type: Array as PropType<Array<ProductReview>>,
      required: true,
    },
    reviewsCount: {
      type: Number,
      required: true,
    },
    rating: {
      type: Number,
      required: true,
    },
    product: {
      type: Object as PropType<ProductVariantExtended>,
      required: true,
    },
  },
  setup(props) {

    const isFormVisible = ref(false);
    const formEmail = ref(null);
    const formRating = ref(null);
    const RatingNumber = computed(() => floorToOneDecimal(props.rating));
    const showReviewForm = () => {

      isFormVisible.value = true;

    };

    const productId = computed(() => productGetters.getId(props.product));

    const getDecodedEmail = (encodedEmail: string | string[]): string => {

      const emailString = Array.isArray(encodedEmail) ? encodedEmail[0] : encodedEmail;

      return window.atob(emailString);

    };

    const getRating = (rawRating: string | string[]): number|null => {

      rawRating = Array.isArray(rawRating) ? rawRating[0] : rawRating;

      return parseInt(rawRating) || null;

    };

    const route = useRoute();

    onMounted(() => {

      if (route.query?.email && route.query?.rating) {

        formEmail.value = getDecodedEmail(route.query?.email);
        formRating.value = getRating(route.query.rating);
        isFormVisible.value = true;

      }

    });

    return {
      reviewGetters,
      RatingNumber,
      isFormVisible,
      showReviewForm,
      REVIEW_ID,
      formEmail,
      formRating,
      productId,
      RATING_REVIEWS_SECTION_ID,
    };

  },
});

</script>
